import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Phone, AlertTriangle, ChevronRight, ChevronDown, Shield,
  Building, Trophy, XCircle, Sparkles, ListTodo
} from 'lucide-react';
import { getClassificationBadge, getWinProbability, formatDealValue } from './utils';

/**
 * Single deal row in the Deals tab. Click to expand and see calls + AI task recs.
 */
export default function DealCard({
  deal,
  isExpanded,
  onToggle,
  onOutcome,
  taskRecs,
  onAcceptTask,
  creatingTaskTitle,
}) {
  const navigate = useNavigate();
  const wp = getWinProbability(deal.score);
  const stageEntries = Object.entries(deal.stage_breakdown || {});

  return (
    <Card
      className={`border-border/50 transition-all ${isExpanded ? 'border-primary/30 shadow-card-hover' : 'hover:border-primary/20'}`}
      data-testid={`deal-card-${deal.company}`}
    >
      <CardContent className="p-5">
        {/* Main deal row - clickable to expand */}
        <div
          className="flex items-start justify-between gap-4 cursor-pointer"
          onClick={(e) => { e.stopPropagation(); onToggle(deal); }}
          data-testid={`deal-toggle-${deal.company}`}
        >
          {/* Left: Deal info */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Row 1: Name + Grade */}
            <div className="flex items-center gap-3 flex-wrap">
              <Building className="w-4 h-4 text-muted-foreground shrink-0" />
              <h3 className="font-heading text-base font-semibold text-foreground truncate">
                {deal.company}
              </h3>
              <Badge className={getClassificationBadge(deal.grade)}>
                {deal.grade}
              </Badge>
              {deal.deal_value && (
                <span className="text-sm font-semibold text-primary">
                  {formatDealValue(deal.deal_value)}
                </span>
              )}
            </div>

            {/* Row 2: Call stages + risks */}
            <div className="flex items-center gap-3 flex-wrap text-xs">
              <div className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-muted-foreground font-medium">{deal.total_calls}</span>
                {stageEntries.map(([stage, count]) => (
                  <span key={stage} className="px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                    {stage} ({count})
                  </span>
                ))}
              </div>

              {deal.risk_count > 0 && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">
                  <AlertTriangle className="w-3 h-3" />
                  {deal.risk_count} risk{deal.risk_count > 1 ? 's' : ''}
                </span>
              )}
              {deal.risk_count === 0 && deal.has_analysis && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-success/10 text-success text-xs">
                  <Shield className="w-3 h-3" />
                  No risks
                </span>
              )}

              {!isExpanded && deal.next_steps?.length > 0 && (
                <span className="text-muted-foreground truncate max-w-[280px]" title={deal.next_steps[0]}>
                  Next: {deal.next_steps[0].length > 45 ? deal.next_steps[0].slice(0, 45) + '...' : deal.next_steps[0]}
                </span>
              )}
            </div>
          </div>

          {/* Right: Win Probability + outcome + expand toggle */}
          <div className="flex items-center gap-3 shrink-0">
            {/* Won/Lost buttons */}
            {deal.outcome === 'won' ? (
              <button
                className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-600 text-xs font-semibold border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
                onClick={(e) => { e.stopPropagation(); onOutcome(deal.company, 'active'); }}
                data-testid={`deal-outcome-won-${deal.company}`}
              >
                <Trophy className="w-3.5 h-3.5" />
                Won
              </button>
            ) : deal.outcome === 'lost' ? (
              <button
                className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/15 text-red-600 text-xs font-semibold border border-red-500/30 hover:bg-red-500/25 transition-colors"
                onClick={(e) => { e.stopPropagation(); onOutcome(deal.company, 'active'); }}
                data-testid={`deal-outcome-lost-${deal.company}`}
              >
                <XCircle className="w-3.5 h-3.5" />
                Lost
              </button>
            ) : (
              <div className="flex gap-1">
                <button
                  className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium text-muted-foreground border border-border hover:bg-emerald-500/10 hover:text-emerald-600 hover:border-emerald-500/30 transition-colors"
                  onClick={(e) => { e.stopPropagation(); onOutcome(deal.company, 'won'); }}
                  data-testid={`deal-set-won-${deal.company}`}
                  title="Mark as Won"
                >
                  <Trophy className="w-3 h-3" />
                  Won
                </button>
                <button
                  className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium text-muted-foreground border border-border hover:bg-red-500/10 hover:text-red-600 hover:border-red-500/30 transition-colors"
                  onClick={(e) => { e.stopPropagation(); onOutcome(deal.company, 'lost'); }}
                  data-testid={`deal-set-lost-${deal.company}`}
                  title="Mark as Lost"
                >
                  <XCircle className="w-3 h-3" />
                  Lost
                </button>
              </div>
            )}

            {deal.has_analysis && (
              <div className="text-right">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Win Prob.</p>
                <p className={`font-heading text-2xl font-bold ${wp.color}`}>{wp.pct}%</p>
                <p className={`text-[10px] font-medium ${wp.color}`}>{wp.label}</p>
              </div>
            )}
            {!deal.has_analysis && (
              <span className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded">Pending</span>
            )}
            {isExpanded
              ? <ChevronDown className="w-5 h-5 text-primary transition-transform" />
              : <ChevronRight className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors" />
            }
          </div>
        </div>

        {/* Expanded: Call details table */}
        {isExpanded && deal.calls && (
          <div className="mt-4 pt-4 border-t border-border/50 animate-fade-in">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`deal-calls-table-${deal.company}`}>
                <thead>
                  <tr className="text-xs text-muted-foreground uppercase tracking-wider">
                    <th className="text-left pb-2 pr-4 font-medium">Call Type</th>
                    <th className="text-left pb-2 pr-4 font-medium">Date</th>
                    <th className="text-left pb-2 pr-4 font-medium">Sentiment</th>
                    <th className="text-left pb-2 pr-4 font-medium">Talk / Listen</th>
                    <th className="text-left pb-2 font-medium">Decision Maker</th>
                    <th className="text-right pb-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/30">
                  {deal.calls.map((c) => {
                    const sentimentColor = c.sentiment === 'Positive' ? 'text-emerald-600 bg-emerald-500/10'
                      : c.sentiment === 'Negative' ? 'text-red-600 bg-red-500/10'
                      : c.sentiment === 'Neutral' ? 'text-amber-600 bg-amber-500/10'
                      : 'text-muted-foreground bg-secondary';
                    const dmColor = c.decision_maker === 'Yes' ? 'text-emerald-600' : 'text-muted-foreground';

                    return (
                      <tr
                        key={c.call_id}
                        className="hover:bg-secondary/40 cursor-pointer transition-colors"
                        onClick={(e) => { e.stopPropagation(); navigate(`/report/${c.call_id}`); }}
                        data-testid={`deal-call-row-${c.call_id}`}
                      >
                        <td className="py-2.5 pr-4">
                          <span className="px-2 py-0.5 rounded bg-secondary text-foreground text-xs font-medium">
                            {c.deal_stage}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-muted-foreground">
                          {c.call_date ? new Date(c.call_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                        </td>
                        <td className="py-2.5 pr-4">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${sentimentColor}`}>
                            {c.sentiment}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-muted-foreground text-xs">
                          {c.talk_ratio}
                        </td>
                        <td className={`py-2.5 text-xs font-medium ${dmColor}`}>
                          {c.decision_maker === 'Yes' ? 'Confirmed' : c.decision_maker === 'No' ? 'Not confirmed' : '-'}
                        </td>
                        <td className="py-2.5 text-right">
                          <ChevronRight className="w-4 h-4 text-muted-foreground inline-block" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* AI Task Recommendations */}
            {(taskRecs || []).length > 0 && (
              <div className="mt-3 pt-3 border-t border-border/30">
                <p className="text-[11px] font-semibold text-primary uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Suggested Tasks
                </p>
                <div className="space-y-1.5">
                  {(taskRecs || []).map((rec, i) => (
                    <div key={`k1-${i}`} className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-primary/[0.04] border border-primary/15">
                      <ListTodo className="w-3 h-3 text-primary shrink-0" />
                      <span className="text-xs text-foreground flex-1 truncate">{rec.title}</span>
                      <button
                        className="text-[10px] font-medium text-primary hover:text-primary/80 shrink-0 px-1.5 py-0.5 rounded hover:bg-primary/10 transition-colors"
                        onClick={(e) => { e.stopPropagation(); onAcceptTask(rec, deal.company); }}
                        disabled={creatingTaskTitle === rec.title}
                      >
                        {creatingTaskTitle === rec.title ? '...' : 'Accept'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
